import { FormControl, Grid, MenuItem, Select } from '@material-ui/core';
import BarChart from 'components/common/chart/BarChart';
import CustomDatePicker from 'components/common/input/CustomDatePicker';
import MuiWrapper from 'components/common/input/MuiWrapper';
import AsyncFetchSelectWithAll from 'components/common/select/AsyncFetchSelectWithAll';
import CustomSpan from 'components/common/text/CustomSpan';
import CustomText from 'components/common/text/CustomText';
import useDateInput from 'lib/hooks/useDateInput';
import useInput from 'lib/hooks/useInput';
import { addCommas, projectStatusChartItemConverter } from 'lib/library';
import { ChartItem } from 'lib/types/dashboardTypes';
import { OffsetPagingData } from 'lib/types/sharedTypes';
import { debounce } from 'lodash';
import moment from 'moment';
import cx from 'classnames';
import React, { useEffect, useMemo, useState } from 'react';
import { DashboardActions, PartnerActions } from 'store/actionCreators';
import { useShallowAppSelector } from 'store/hooks';
import styled from 'styled-components';
import { color } from 'styles/utils';
import { gridItemHorizentalMargin, gridItemVerticalMargin } from 'styles/_variables';
import { useDidUpdateEffect } from 'lib/utils';
import { useTranslation } from 'react-i18next';
import T from 'components/common/text/T';
import { isNormalLevelUserSelector } from 'store/modules/user';
import { img_banner01, btn_arrow } from 'components/base/images';
import CircularLoading from 'components/base/loading/CircularLoading';
import ListEmpty from 'components/common/list/ListEmpty';
import { PageSubTitle } from 'components/base/title/PageTitle';

interface SalesContainerProps {
  className?: string;
}

interface InvoiceGraphItem {
  currency: string;
  dueMonth: string;
  dueWeek: string;
  dueYear: string;
  partnerGroupIdx: number;
  projectCount: number;
  total: number;
}

interface IInvoicePartner {
  email: string;
  isSelf: number;
  name: string;
  userGroupIdx: number;
}

function SalesContainer({ className }: SalesContainerProps) {
  const {
    user: { currencyCode },
    fetchCurrencyList: { currencyList },
    invoicePartnersData: { list: invoicePartnersList, pagingData: invoicePartnersPagingData },
    invoicePartnersSuccess,
    invoicePartnersPending,
    salesGraphData: { invoiceGraph: invoiceGraphData, projectCount = 0, totalSales = 0 },
    salesGraphSuccess,
    salesGraphPending,
    salesGraphFailure,
    isNormalLevelUser,
    language,
  }: {
    user: { currencyCode: string };
    fetchCurrencyList: { currencyList: any };
    invoicePartnersData: { list: IInvoicePartner[]; pagingData: OffsetPagingData };
    invoicePartnersSuccess: boolean;
    invoicePartnersPending: boolean;
    salesGraphData: { invoiceGraph: InvoiceGraphItem[]; projectCount: number; totalSales: number };
    salesGraphSuccess: boolean;
    salesGraphPending: boolean;
    salesGraphFailure: boolean;
    isNormalLevelUser: boolean;
    language: string;
  } = useShallowAppSelector(state => ({
    user: state.user.user || {},
    fetchCurrencyList: state.util.currencyList.data || {},
    invoicePartnersData: state.partner.invoicePartners.data || {},
    invoicePartnersSuccess: state.partner.invoicePartners.success,
    invoicePartnersPending: state.partner.invoicePartners.pending,
    salesGraphData: state.dashboard.salesGraph.data || {},
    salesGraphSuccess: state.dashboard.salesGraph.success,
    salesGraphPending: state.dashboard.salesGraph.pending,
    salesGraphFailure: state.dashboard.salesGraph.failure,
    isNormalLevelUser: isNormalLevelUserSelector(state),
    language: state.base.language,
  }));

  const [isLoadSuccess, setIsLoadSuccess] = useState<boolean>(false);
  const [isLoadFailure, setIsLoadFailure] = useState<boolean>(false);
  const { t } = useTranslation();
  const [invoicePartners, setInvoicePartners] = useState<IInvoicePartner[]>([]);
  const setInvoicePartnersValue = useMemo(() => {
    return invoicePartnersSuccess && setInvoicePartners(draft => draft.concat(invoicePartnersList));
  }, [!!invoicePartnersSuccess]);
  const hasMoreInvoicePartners =
    invoicePartnersPagingData?.page < invoicePartnersPagingData?.totalPage;
  const [invoiceGraph, setInvoiceGraph] = useState<ChartItem[]>([]);
  // 1Y, 1M, 1W
  const period = useDateInput([moment().subtract(6, 'days'), moment()]);
  const salesCurrency = useInput(currencyCode); // 'KRW', 'USD'
  const currencyFeature = useInput(
    currencyList?.find((item: any) => item.code === currencyCode)?.feature || '￦',
  ); // 'KRW', 'USD'
  const salesPartnerKeyword = useInput('');
  const selectedSalesPartner = useInput('');

  // SECTION: function

  const onFetchInvoicePartners = debounce((first?: boolean) => {
    if (first) return PartnerActions.fetch_invoice_partners_request({ keyword: '', page: 1 });

    const { page, totalPage } = invoicePartnersPagingData;
    if (page >= totalPage) return;
    PartnerActions.fetch_invoice_partners_request({
      keyword: salesPartnerKeyword.value || '',
      page: page + 1,
    });
  }, 500);

  const onSearchInvoicePartners = debounce((value: string) => {
    salesPartnerKeyword.setValue(value);
    setInvoicePartners([]);
    PartnerActions.fetch_invoice_partners_request({ keyword: value || '', page: 1 });
  }, 500);

  useEffect(() => {
    PartnerActions.fetch_invoice_partners_request({ keyword: '', page: 1 });
  }, []);

  useEffect(() => {
    return () => {
      setIsLoadFailure(false);
      setIsLoadSuccess(false);
    };
  }, []);

  useEffect(() => {
    if (period.value?.length === 2) {
      const diffDay = period.value[1].diff(period.value[0], 'days');

      // maxDate 365
      if (diffDay > 364) {
        const startDateValue = moment(period.value[0]);
        const endDateValue = moment(period.value[0]).add(364, 'days');
        return period.setValue([startDateValue, endDateValue]);
      }

      DashboardActions.fetch_sales_graph_request({
        startDate: period.value ? period.value[0].format('YYYY-MM-DD') : null,
        endDate: period.value ? period.value[1].format('YYYY-MM-DD') : null,
        currency: salesCurrency.value,
        partnerGroupIdx: selectedSalesPartner.value,
      });
    }
  }, [period.value, selectedSalesPartner.value, salesCurrency.value]);

  useEffect(() => {
    if (!salesGraphSuccess) return;
    if (period.value?.length !== 2) return;
    const invoiceGraphDataParse = invoiceGraphData.reduce(
      (acc: any[], curr: any, index: number) => {
        const findIndex = acc.findIndex(item => item.groupDate === curr.groupDate);
        if (findIndex === -1) {
          acc.push({
            groupDate: curr.groupDate,
            totalSales: curr.totalSales,
            projectCount: curr.projectCount,
            currency: curr.currency,
          });
        } else {
          acc[findIndex].totalSales = acc[findIndex].totalSales + curr.totalSales;
          acc[findIndex].projectCount = acc[findIndex].projectCount + curr.projectCount;
        }
        return acc;
      },
      [],
    );
    const invoiceGraphValue: ChartItem[] | undefined = projectStatusChartItemConverter({
      items: invoiceGraphDataParse,
      startDate: period.value[0],
      endDate: period.value[1],
      yKey: 'totalSales',
    });
    setInvoiceGraph(invoiceGraphValue || []);
  }, [salesGraphSuccess]);

  // NOTE: 차트쪽 데이터 업데이트시 애니메이션 적용을 위해 적용
  const [totalSalesData, setTotalSalesData] = useState<object[]>([]);
  const handleControlPriceUnit = (value: number) => {
    let data: string = '';
    if (salesCurrency.value === 'KRW') {
      // data = String(value).substring(0, String(value).length - 3);
      data = String(value);
    } else {
      data = String(value);
    }
    return addCommas(Number(value));
  };

  const handleRefreshList = () => {
    DashboardActions.fetch_sales_graph_request({
      startDate: period.value ? period.value[0].format('YYYY-MM-DD') : null,
      endDate: period.value ? period.value[1].format('YYYY-MM-DD') : null,
      currency: salesCurrency.value,
      partnerGroupIdx: selectedSalesPartner.value,
    });
    setIsLoadSuccess(false);
    setIsLoadFailure(false);
  };

  useEffect(() => {
    const invoiceGraphValue = invoiceGraph.map(item => {
      let returnValue = {};

      // if (salesCurrency.value === 'KRW') {
      //   returnValue = {
      //     ...item,
      //     graphValue: item.y / 1000,
      //   };
      // } else if (salesCurrency.value === 'JPY') {
      //   returnValue = {
      //     ...item,
      //     graphValue: item.y / 100,
      //   };
      // } else {
      //   returnValue = { ...item, graphValue: item.y };
      // }
      switch (salesCurrency.value) {
        case 'KRW':
          returnValue = {
            ...item,
            graphValue: item.y / 1000,
          };
          break;
        case 'JPY':
          returnValue = {
            ...item,
            graphValue: item.y / 100,
          };
          break;
        default:
          returnValue = { ...item, graphValue: item.y };
          break;
      }
      return returnValue;
    });

    const animation = setTimeout(() => setTotalSalesData(invoiceGraphValue), 1);
    return () => {
      clearTimeout(animation);
    };
  }, [invoiceGraph, salesCurrency.value]);

  useEffect(() => {
    console.log('HDS] totalSalesData ___ ', totalSalesData);
  }, [totalSalesData]);

  useDidUpdateEffect(() => {
    currencyFeature.setValue(
      currencyList?.find((item: any) => item.code === salesCurrency.value)?.feature,
    );
  }, [salesCurrency.value]);

  useDidUpdateEffect(() => {
    if (!salesGraphSuccess) return;
    setIsLoadSuccess(true);
  }, [salesGraphSuccess]);
  useDidUpdateEffect(() => {
    if (!salesGraphFailure) return;
    setIsLoadFailure(true);
  }, [salesGraphFailure]);

  return (
    <StyledSales data-component-name="Sales" item className={className} lang={language}>
      <div className="dashboard__card_title_box">
        <h2 className="dashboard__card_title">
          <PageSubTitle
            title={!isNormalLevelUser ? <T>HOME_TITLE_SALES</T> : <T>{`ㅤ`}</T>}
            margin="0"
          />
        </h2>
      </div>

      <div className="dashboard__sales_chart_container box-shadow-default">
        {!isNormalLevelUser ? (
          <>
            <div className="dashboard__sales_option_box">
              <div className="dashboard__sales_option_list">
                <div className="dashboard__sales_option_due">
                  <CustomSpan fontSize={15} fontWeight={700}>
                    <T>GLOBAL_PERIOD</T>
                  </CustomSpan>
                  <CustomDatePicker
                    type="rangeDate"
                    testId="sales_date_picker"
                    fullWidth
                    height={32}
                    fontSize={12}
                    value={period.value}
                    onChange={period.onChange}
                    className={cx({
                      error: period.value !== null ? !period.value : false,
                    })}
                  />
                </div>

                <div className="dashboard__sales_option_partner">
                  <CustomSpan fontSize={15} fontWeight={700}>
                    <T>GLOBAL_PARTNER</T>
                  </CustomSpan>
                  <AsyncFetchSelectWithAll
                    className="sm"
                    fullWidth
                    inputProps={{
                      hasInitialValue: true,
                      selectedValue: selectedSalesPartner.value,
                      setSelectedValue: selectedSalesPartner.setValue,
                      data: invoicePartners,
                      hasMoreData: hasMoreInvoicePartners,
                      initialValueName: t('GLOBAL_ALL'),
                      idKey: 'userGroupIdx',
                      labelKey: 'name',
                      onFetch: onFetchInvoicePartners,
                      searchLoading: invoicePartnersPending,
                      onSearch: onSearchInvoicePartners,
                    }}
                    testId="sales_partner"
                  />
                </div>
                <div className="dashboard__sales_option_currency">
                  <CustomSpan fontSize={15} fontWeight={700}>
                    <T>GLOBAL_CURRENCY</T>
                  </CustomSpan>
                  {currencyList?.length > 0 && (
                    <MuiWrapper isGlobalStyle className="sm">
                      <FormControl fullWidth variant="outlined">
                        <Select
                          MenuProps={{
                            anchorOrigin: {
                              vertical: 'bottom',
                              horizontal: 'left',
                            },
                            getContentAnchorEl: null,
                          }}
                          data-testid="sales_currency_drop"
                          name="salesCurrency"
                          value={salesCurrency.value || ''}
                          onChange={salesCurrency.onChange}
                        >
                          {currencyList?.map((item: any, index: number) => {
                            return (
                              <MenuItem key={index} value={item.code}>
                                {item.feature} {item.name}
                              </MenuItem>
                            );
                          })}
                        </Select>
                      </FormControl>
                    </MuiWrapper>
                  )}
                </div>
              </div>
            </div>
            <div className="dashboard__sales_contents_box">
              <div className="dashboard__sales_chart_box">
                {!isLoadSuccess && !!salesGraphPending && (
                  <div className="infinite-scroll-loading">
                    <CircularLoading />
                  </div>
                )}
                {!!isLoadSuccess && (
                  <div className="dashboard__sales_chart">
                    <>
                      <span className="dashboard__sales_chart_unit">
                        {salesCurrency.value === 'KRW' &&
                          `(${t('SALES_UNIT')} : ${t('SALES_ONE_THOUSAND_WON')})`}
                        {salesCurrency.value === 'USD' &&
                          `(${t('SALES_UNIT')} : ${t('SALES_ONE_DOLLAR')})`}
                        {salesCurrency.value === 'JPY' &&
                          `(${t('SALES_UNIT')} : ${t('SALES_ONE_HUNDRED_YEN')})`}
                      </span>
                      <BarChart
                        width={language === 'en' ? 583 : 600}
                        height={265}
                        isRenderTick
                        padding={0.8}
                        data={totalSalesData}
                        axisLeftFormat={handleControlPriceUnit}
                        axisBottom={{
                          tickRotation: 0,
                        }}
                        margin={{
                          top: 10,
                          right: language === 'en' ? 20 : 30,
                          bottom: 50,
                          left: language === 'en' ? 83 : 70,
                        }}
                      />
                    </>
                  </div>
                )}
                {!!isLoadFailure && (
                  <ListEmpty
                    text="GLOBAL_LIST_ERROR"
                    isRefreshButton={true}
                    onRefresh={handleRefreshList}
                  />
                )}
              </div>

              <div className="dashboard__sales_option_info_box">
                <div className="dashboard__sales_option_info">
                  <div className="dashboard__sales_option_info_item">
                    <CustomText fontSize={15} fontColor="#444" fontWeight={500} width={80}>
                      <T>GLOBAL_PROJECTS</T>
                    </CustomText>
                    <CustomText
                      fontSize={14}
                      fontColor="#00a6e2"
                      style={{ textAlign: 'right' }}
                      testId="sales_project_count"
                    >
                      {!!isLoadSuccess ? projectCount : '0'}
                    </CustomText>
                  </div>
                </div>
                <div className="dashboard__sales_option_info">
                  <div className="dashboard__sales_option_info_item">
                    <CustomText fontSize={15} fontColor="#444" fontWeight={500} width={80}>
                      <T>GLOBAL_SALES</T>
                    </CustomText>
                    <CustomText
                      fontSize={14}
                      fontColor="#00a6e2"
                      style={{ textAlign: 'right' }}
                      testId="sales_amount"
                    >
                      {currencyFeature.value} {!!isLoadSuccess ? `${addCommas(totalSales)}` : '0'}
                    </CustomText>
                  </div>
                </div>
              </div>
            </div>
          </>
        ) : (
          <a href="https://doflab.com/ko/craft5x/" target="_blank">
            <div className="dashboard__sales_img">
              <div className="dashboard__sales_img_box">
                <img src={img_banner01}></img>
              </div>
              <div className="dashboard__sales_img_text">
                <CustomText fontSize={16} fontColor="#222" marginLeft={30} marginBottom={10}>
                  <T>HOME_BANNER_TITLE_CRAFT_5X</T>
                </CustomText>
                <CustomText
                  fontSize={42}
                  fontColor="#00155e"
                  marginLeft={30}
                  marginBottom={42}
                  fontWeight="bold"
                >
                  CRAFT 5X & DC
                </CustomText>
                <div className="dashboard_sales_img_text_viewmore">
                  <CustomSpan
                    fontSize={16}
                    fontColor="#1b2960"
                    marginLeft={30}
                    marginRight={14}
                    fontWeight="normal"
                  >
                    <T>View More</T>
                  </CustomSpan>
                  <img src={btn_arrow} width={20} height={12} />
                </div>
              </div>
            </div>
          </a>
        )}
      </div>
    </StyledSales>
  );
}

const StyledSales = styled(Grid)`
  width: 810px;
  .dashboard__sales_total {
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    top: ${gridItemVerticalMargin}px;
    right: ${gridItemHorizentalMargin}px;
    margin-bottom: -15px;
    height: 90px;
    padding-bottom: 15px;
    background-color: ${color.blue};
    box-shadow: inset 0px 3px 6px rgba(0, 0, 0, 0.16);
    border-top-left-radius: 15px;
    border-top-right-radius: 15px;
    font-size: 21px;
    color: white;
    text-align: center;
  }
  .dashboard__sales_chart_container {
    display: flex;
    justify-content: space-between;
    position: relative;
    height: 360px;
    background-color: white;
    border-radius: 10px;
    flex-direction: column;
    padding: 30px 0 30px 0;
    .dashboard__sales_option_box {
      display: flex;
      justify-content: space-between;
      padding: 0 30px 0 30px;
      .dashboard__sales_option_list {
        white-space: nowrap;
        display: flex;
        justify-content: space-between;
        width: 100%;
        .dashboard__sales_option_due,
        .dashboard__sales_option_partner,
        .dashboard__sales_option_currency {
          align-items: center;
          display: flex;
          column-gap: 10px;
        }

        .datePicker__box {
          width: ${props => (props.lang === 'en' ? 210 : 210)}px;
          .ant-picker-input {
            width: 65px;
          }
        }
        .asyncFetchSelect {
          width: ${props => (props.lang === 'en' ? 190 : 210)}px;
        }
        .muiWrapper {
          width: ${props => (props.lang === 'en' ? 110 : 125)}px;
        }
      }
      .dashboard__sales_option_material {
        position: relative;
      }
      // materialConfigModal
      .dashboard__sales_material_config_modal {
        z-index: 1;
        position: absolute;
        top: 53px;
        left: 0px;
        width: 1065px;
        background-color: white;
        border-radius: 15px;
        box-shadow: 0 0 15px rgba(0, 0, 0, 0.16);
        .materialConfigModal__header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          position: relative;
          padding: 9px 20px;
          background-color: ${color.navy_blue};
          border-top-left-radius: 10px;
          border-top-right-radius: 10px;
          font-size: 15px;
          font-weight: 500;
          color: white;

          .materialConfigModal__close_btn {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 30px;
            height: 30px;
            border: 1px solid white;
            border-radius: 50%;
            font-size: 22px;
          }
        }
        .materialConfigModal__body {
          padding: 30px;
          padding-top: 0;
          border-bottom-left-radius: 10px;
          border-bottom-right-radius: 10px;

          .materialConfigModal__selected_list {
            margin-top: 25px;
            padding: 0 12px;
            background-color: #fafafd;
            border-radius: 15px;
            .materialConfigModal__selected_item {
              display: flex;
              align-items: center;
              padding: 10px 0;
              &:not(:first-child) {
                border-top: 1px solid #d6dce0;
              }
              .materialConfigModal__selected_item_label {
                width: 220px;
                padding-left: 10px;
                font-size: 13px;
                font-weight: 500;
              }
              .materialConfigModal__selected_item_meterial_list {
                width: calc(100% - 220px);
                padding-left: 5px;
                .materialConfigModal__selected_item_meterial_item {
                  display: inline-flex;
                  align-items: center;
                  justify-content: space-between;
                  margin: 0 5px;
                  width: 110px;
                  padding: 5px 10px;
                  padding-right: 5px;
                  border-radius: 15px;
                  font-size: 10px;
                  color: white;
                  &:nth-child(6) ~ * {
                    margin-top: 5px;
                  }
                  > p {
                    width: 95px;
                    white-space: nowrap;
                    text-overflow: ellipsis;
                    overflow: hidden;
                  }
                  .materialConfigModal__selected_item_meterial_item_delete_btn {
                    font-size: 12px;
                  }
                }
              }
            }
          }
          .materialConfigModal__select_list {
            display: flex;
            flex-wrap: wrap;
            .materialConfigModal__select_item {
              width: 240px;
              margin-top: 30px;
              &:not(:nth-child(4n + 1)) {
                margin-left: 15px;
              }
              .muiWrapper {
                margin-top: 10px;
              }
            }
          }
        }
      }
    }
    .dashboard__sales_contents_box {
      display: flex;
      .dashboard__sales_option_info_box {
        padding-right: 30px;
        .dashboard__sales_option_info {
          margin-top: 20px;
          padding: 20px;
          width: ${props => (props.lang === 'en' ? 180 : 160)}px;
          height: 116px;
          background-color: #fcfcfc;
          display: flex;
          flex-direction: column;
          row-gap: 7px;
          border-radius: 5px;
          border: solid 1px #e5e5e5;
          .dashboard__sales_option_info_item {
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            height: 100%;
          }
        }
      }

      .dashboard__sales_chart_box {
        position: relative;
        display: flex;
        align-items: center;
        width: 100%;
        height: 100%;
        margin-top: 10px;
        .dashboard__sales_chart {
          display: flex;
          flex-direction: column;
          .dashboard__sales_chart_unit {
            display: flex;
            font-size: 13px;
            color: #888888;
            font-weight: 200;
            justify-content: flex-end;
            margin-right: 30px;
          }
        }
      }
    }
  }
  .dashboard__sales_img {
    .dashboard__sales_img_box {
      img {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        vertical-align: middle;
      }
    }
    .dashboard__sales_img_text {
      position: absolute;
      top: 50%;
      left: 50%;
      width: 100%;
      transform: translate(-50%, -50%);
    }
  }
`;

export default React.memo(SalesContainer);
